import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Button, Space, Input, Row, Col, Tooltip, Popconfirm,
  Modal, Form, Select, Divider,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, StopOutlined, PlayCircleOutlined,
  UserOutlined, GlobalOutlined, TeamOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  listerAgents, creerAgent, suspendrAgent, reactiverAgent,
  STATUTS_AGENT,
} from '../../services/agentVerbalisateurService';
import { PAYS_CEDEAO } from '../../variables/paysCedeao';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

const { Option } = Select;

export default function AgentsVerbalisateursPage() {
  const { t } = useTranslation('agentsVerbalisateurs');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filtrePays, setFiltrePays] = useState(null);
  const [modalCreer, setModalCreer] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listerAgents();
      setAgents(list);
    } catch {
      toast.error(t('messages.erreurChargement'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action, trackingId) => {
    try {
      if (action === 'suspendre') await suspendrAgent(trackingId);
      else if (action === 'reactiver') await reactiverAgent(trackingId);
      toast.success(action === 'suspendre' ? t('messages.suspendu') : t('messages.reactive'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.erreurAction'));
    }
  };

  const filtered = agents.filter((a) => {
    const matchSearch = !search ||
      a.nom?.toLowerCase().includes(search.toLowerCase()) ||
      a.prenom?.toLowerCase().includes(search.toLowerCase()) ||
      a.matricule?.toLowerCase().includes(search.toLowerCase());
    const matchPays = !filtrePays || a.paysCode === filtrePays;
    return matchSearch && matchPays;
  });

  const columns = [
    {
      title: t('colonnes.matricule'),
      dataIndex: 'matricule',
      key: 'matricule',
      render: (val) => <code style={{ fontSize: 12 }}>{val}</code>,
    },
    {
      title: t('colonnes.nomComplet'),
      key: 'nom',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 600 }}>{rec.nom} {rec.prenom || ''}</div>
          {rec.fonction && <div style={{ fontSize: 11, color: '#6b7280' }}>{rec.fonction}</div>}
        </div>
      ),
    },
    {
      title: t('colonnes.pays'),
      dataIndex: 'paysCode',
      key: 'pays',
      render: (val) => {
        const pays = PAYS_CEDEAO.find((p) => p.code === val);
        return <span><GlobalOutlined style={{ marginRight: 4 }} />{pays ? `${pays.flag} ${pays.nom}` : (val || '-')}</span>;
      },
    },
    {
      title: t('colonnes.service'),
      dataIndex: 'serviceAffectation',
      key: 'service',
      render: (val) => val || '-',
    },
    {
      title: t('colonnes.contact'),
      key: 'contact',
      render: (_, rec) => (
        <div style={{ fontSize: 12 }}>
          {rec.telephone && <div>{rec.telephone}</div>}
          {rec.email && <div style={{ color: '#6b7280' }}>{rec.email}</div>}
        </div>
      ),
    },
    {
      title: t('colonnes.statut'),
      dataIndex: 'statut',
      key: 'statut',
      render: (val) => {
        const s = STATUTS_AGENT[val] || { label: val, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: t('colonnes.derniereActivite'),
      dataIndex: 'derniereConnexionAt',
      key: 'connexion',
      render: (val) => val ? new Date(val).toLocaleDateString(localeIntl()) : <span style={{ color: '#9ca3af' }}>{t('colonnes.jamais')}</span>,
    },
    {
      title: t('colonnes.actions'),
      key: 'actions',
      width: 100,
      render: (_, rec) => (
        <Space size={4}>
          {rec.statut === 'ACTIF' && (
            <Tooltip title={t('actions.suspendre')}>
              <Popconfirm title={t('actions.confirmerSuspension')}
                onConfirm={() => handleAction('suspendre', rec.agentTrackingId)}>
                <Button size="small" icon={<StopOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          {rec.statut === 'SUSPENDU' && (
            <Tooltip title={t('actions.reactiver')}>
              <Button size="small" icon={<PlayCircleOutlined />}
                onClick={() => handleAction('reactiver', rec.agentTrackingId)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout title={t('titreOnglet')}>
      {/* Recherche et filtre pays sont appliques cote client : `filtered.length`
          est le seul nombre qui corresponde aux lignes reellement affichees. */}
      <PageHeader
        titre={t('titre')}
        icone={<TeamOutlined />}
        compte={filtered.length}
        unite={t('unite')}
        actions={(
          <Button
            type="primary" icon={<PlusOutlined />}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            onClick={() => setModalCreer(true)}>
            {t('actions.nouveau')}
          </Button>
        )}
      />

      <KpiCards
        items={[
          { label: t('kpi.total'), value: agents.length, icon: <TeamOutlined />, color: '#1a6b3c', bg: '#e8f5ed' },
          { label: t('kpi.actifs'), value: agents.filter((a) => a.statut === 'ACTIF').length, icon: <CheckCircleOutlined />, color: '#166534', bg: '#dcfce7' },
          { label: t('kpi.suspendus'), value: agents.filter((a) => a.statut === 'SUSPENDU').length, icon: <StopOutlined />, color: '#92400e', bg: '#fef3c7' },
          { label: t('kpi.paysCouverts'), value: new Set(agents.map((a) => a.paysCode).filter(Boolean)).size, icon: <GlobalOutlined />, color: '#6b7280', bg: '#f3f4f6' },
        ]}
      />

      <div className="cb-card" style={{ padding: 16 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={10}>
            <Input
              placeholder={t('filtres.recherche')}
              prefix={<UserOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} md={6}>
            <Select
              placeholder={t('colonnes.pays')} allowClear style={{ width: '100%' }}
              value={filtrePays}
              onChange={setFiltrePays}>
              {PAYS_CEDEAO.map((p) => (
                <Option key={p.code} value={p.code}>{p.flag} {p.nom}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Button icon={<ReloadOutlined />} onClick={load} block>{t('actions.actualiser')}</Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="agentTrackingId"
          loading={loading}
          pagination={{ pageSize: 20, showTotal: (total) => t('total', { total }) }}
          size="small"
        />
      </div>

      <CreerAgentModal
        open={modalCreer}
        onClose={() => setModalCreer(false)}
        onCreated={() => { setModalCreer(false); load(); }}
      />
    </Layout>
  );
}

function CreerAgentModal({ open, onClose, onCreated }) {
  const { t } = useTranslation('agentsVerbalisateurs');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const result = await creerAgent(values);
      toast.success(t('creer.succes', { matricule: result?.matricule || '' }));
      onCreated(result);
    } catch (e) {
      if (e?.errorFields) return;
      toast.error(e?.response?.data?.message || t('creer.erreur'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('creer.titre')}
      open={open} onCancel={onClose} width={560}
      footer={
        <Space>
          <Button onClick={onClose}>{t('actions.annuler')}</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('creer.creer')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Divider orientation="left" style={{ fontSize: 12 }}>{t('creer.sections.identite')}</Divider>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="nom" label={t('creer.nom')} rules={[{ required: true }]}>
              <Input placeholder={t('creer.nomPlaceholder')} style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="prenom" label={t('creer.prenoms')}>
              <Input placeholder={t('creer.prenoms')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="telephone" label={t('creer.telephone')}>
              <Input placeholder="+228 XX XX XX XX" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="email" label={t('creer.email')}>
              <Input type="email" placeholder="agent@cbconnect.tg" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ fontSize: 12 }}>{t('creer.sections.affectation')}</Divider>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="paysCode" label={t('creer.paysAffectation')} rules={[{ required: true }]}>
              <Select placeholder={t('creer.paysPlaceholder')} showSearch optionFilterProp="children">
                {PAYS_CEDEAO.map((p) => (
                  <Option key={p.code} value={p.code}>{p.flag} {p.nom}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="serviceAffectation" label={t('creer.service')}>
              <Input placeholder={t('creer.servicePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ fontSize: 12 }}>{t('creer.sections.authentification')}</Divider>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="matricule" label={t('colonnes.matricule')} rules={[{ required: true }]}>
              <Input placeholder="AG-TGO-0001" style={{ fontFamily: 'var(--font-main)' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="pin" label={t('creer.pin')}
              rules={[{ required: true }, { pattern: /^\d{6}$/, message: t('creer.pinRequis') }]}>
              <Input.Password placeholder="XXXXXX" maxLength={6} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
